import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { toast } from 'sonner';
import { liveSyncApi } from '../services/liveSyncApi';
import { getYoutubeEmbedUrl } from '../utils/urlUtils';

interface LiveSyncControlProps {
    tableId: string;
    isOpen: boolean;
    onOpenChange: (open: boolean) => void;
}

export function LiveSyncControl({ tableId, isOpen, onOpenChange }: LiveSyncControlProps) {
    const [type, setType] = useState<'url' | 'embed'>('url');
    const [content, setContent] = useState('');
    const [broadcastType, setBroadcastType] = useState<'phet' | 'video' | 'custom'>('custom');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleShare = async () => {
        if (!content.trim()) {
            toast.error('Por favor, insira o link ou código de embed.');
            return;
        }

        setIsSubmitting(true);
        try {
            const urlToShare = (type === 'url' && broadcastType === 'video')
                ? getYoutubeEmbedUrl(content)
                : (type === 'url' ? content : undefined);

            await liveSyncApi.startLiveSync(tableId, {
                url: urlToShare,
                embedCode: type === 'embed' ? content : undefined,
                type: broadcastType
            });
            toast.success('Sincronização iniciada com sucesso!');
            onOpenChange(false);
            setContent('');
        } catch (error) {
            console.error('Erro ao iniciar sincronização:', error);
            toast.error('Não foi possível iniciar a sincronização.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Dialog open={isOpen} onOpenChange={onOpenChange}>
            <DialogContent className="bg-game-secondary border-game-primary max-w-md">
                <DialogHeader>
                    <DialogTitle className="text-game-primary">Nova Sincronização</DialogTitle>
                </DialogHeader>

                <div className="space-y-6 py-4">
                    <div className="space-y-3">
                        <Label className="text-game-primary">O que você deseja compartilhar?</Label>
                        <RadioGroup
                            value={type}
                            onValueChange={(v: any) => setType(v)}
                            className="flex gap-4"
                        >
                            <div className="flex items-center space-x-2">
                                <RadioGroupItem value="url" id="url" />
                                <Label htmlFor="url" className="text-game-secondary cursor-pointer">Link Direto</Label>
                            </div>
                            <div className="flex items-center space-x-2">
                                <RadioGroupItem value="embed" id="embed" />
                                <Label htmlFor="embed" className="text-game-secondary cursor-pointer">Código Embed (iFrame)</Label>
                            </div>
                        </RadioGroup>
                    </div>

                    <div className="space-y-3">
                        <Label className="text-game-primary">Tipo de Conteúdo</Label>
                        <RadioGroup
                            value={broadcastType}
                            onValueChange={(v: any) => setBroadcastType(v)}
                            className="flex flex-wrap gap-4"
                        >
                            <div className="flex items-center space-x-2">
                                <RadioGroupItem value="phet" id="phet" />
                                <Label htmlFor="phet" className="text-game-secondary cursor-pointer">PhET</Label>
                            </div>
                            <div className="flex items-center space-x-2">
                                <RadioGroupItem value="video" id="video" />
                                <Label htmlFor="video" className="text-game-secondary cursor-pointer">Vídeo</Label>
                            </div>
                            <div className="flex items-center space-x-2">
                                <RadioGroupItem value="custom" id="custom" />
                                <Label htmlFor="custom" className="text-game-secondary cursor-pointer">Outro</Label>
                            </div>
                        </RadioGroup>
                    </div>

                    <div className="space-y-3">
                        <Label className="text-game-primary">
                            {type === 'url' ? 'URL do Conteúdo' : 'Cole o iFrame completo'}
                        </Label>
                        {type === 'url' ? (
                            <Input
                                placeholder="https://phet.colorado.edu/..."
                                value={content}
                                onChange={(e) => setContent(e.target.value)}
                                className="bg-game-dark border-game-hover text-game-primary"
                            />
                        ) : (
                            <Textarea
                                placeholder="<iframe src='...' />"
                                value={content}
                                onChange={(e) => setContent(e.target.value)}
                                className="bg-game-dark border-game-hover text-game-primary min-h-[100px]"
                            />
                        )}
                        <p className="text-[10px] text-zinc-500 italic">
                            Este conteúdo será aberto automaticamente para todos os jogadores na mesa.
                        </p>
                    </div>
                </div>

                <DialogFooter>
                    <Button variant="ghost" onClick={() => onOpenChange(false)} className="text-game-muted">
                        Cancelar
                    </Button>
                    <Button
                        onClick={handleShare}
                        disabled={isSubmitting}
                        className="bg-violet-600 hover:bg-violet-700 text-white"
                    >
                        {isSubmitting ? 'Iniciando...' : 'Transmitir para Todos'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
